import React, { forwardRef } from "react";

import Chip from "@mui/material/Chip";
import Container, { ContainerProps } from "@mui/material/Container";
import Divider, { DividerProps } from "@mui/material/Divider";
import styled from "@mui/material/styles/styled";
import Typography, { TypographyProps } from "@mui/material/Typography";

import { MOBILE_NAV_HEIGHT_PX } from "@app/analytics/components/MobileNav/styles";

import Stack, { Column, Row, StackProps } from "@rui/Stack";
import TooltipWrapper from "@rui/TooltipWrapper";

export const BaseOuterContainer = styled(Stack)(({ theme }) => ({
  width: "100%",
  flexDirection: "column",
  justifyContent: "stretch",
  alignItems: "stretch",
  minHeight: "500px",
  height: `calc(100vh - ${MOBILE_NAV_HEIGHT_PX})`,
  backgroundColor: "#FFF",
  position: "relative",
  overflowX: "hidden",
  [theme.breakpoints.up("mobile")]: {
    height: "100vh",
  },
}));

export const InnerContainer = styled(Stack)(({ theme }) => ({
  flexDirection: "column-reverse",
  flexGrow: 1,
  [theme.breakpoints.up("md")]: {
    flexDirection: "row",
  },
}));

export const ContentColumnContainer = styled(Stack)({
  height: "100%",
  width: "100%",
  overflow: "hidden",
  position: "relative",
});

export const ContentColumn = styled(Stack)({
  flexGrow: 1,
  height: "100%",
  maxHeight: "100%",
  backgroundColor: "#FFF",
  overflow: "auto",
  position: "relative",
});

export const ContentSection = styled(Stack)(({ theme }) => ({
  [theme.breakpoints.up("mobile")]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}));

export const SectionContainer = styled((props: ContainerProps) => (
  <Container maxWidth="xl" disableGutters {...props} />
))(({ theme }) => ({
  paddingLeft: theme.spaces.base,
  paddingRight: theme.spaces.base,
}));

export const SectionDivider = (props: DividerProps) => (
  <Container maxWidth="xl" disableGutters sx={{ px: 2 }}>
    <Divider {...props} />
  </Container>
);

export const SidebarContentOuterContainer = styled(Column)(({ theme }) => ({
  display: "none",
  [theme.breakpoints.up("md")]: {
    display: "flex",
  },
  maxHeight: "100%",
  overflow: "hidden",
  position: "relative",
}));

export const SidebarContentInnerContainer = styled(Column)(({ theme }) => ({
  maxHeight: "100%",
  overflow: "auto",
  borderRadius: "8px",
  paddingBottom: theme.spacing(2),
}));

export const SidebarHeader = styled(({ children, ...props }: StackProps) => (
  <Column {...props}>
    <Row variant="apart" sx={{ mb: "-2px" }}>
      {children}
    </Row>
  </Column>
))(({ theme }) => ({
  top: 0,
  position: "sticky",
  backgroundColor: theme.palette.shades.ghost[300],
  borderBottom: `2px solid ${theme.palette.shades.neutral[100]}`,
  zIndex: 1,
}));

type VerticalDividerProps = Omit<DividerProps, "orientation"> & {
  size: "sm" | "md";
};

const HEIGHT_BY_SIZE = {
  sm: "14px",
  md: "24px",
};

export const VerticalDivider = styled<React.FC<VerticalDividerProps>>(
  ({ size: _s, ...props }) => <Divider orientation="vertical" {...props} />
)(({ size }) => ({
  height: HEIGHT_BY_SIZE[size],
}));

export const BaseHeaderContainer = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  backgroundColor: "#FFF",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  [theme.breakpoints.up("mobile")]: {
    padding: theme.spacing(1.5, 2),
  },
  color: theme.palette.shades.neutral[800],
}));

export const HeaderDetailsContainer = styled<React.FC<StackProps>>((props) => (
  <Row variant="left" spacing={1} {...props} />
))(({ theme }) => ({
  height: "16px",
  flexWrap: "wrap",
  overflow: "hidden",
  [theme.breakpoints.up("mobile")]: {
    height: "20px",
  },
}));

export const HeaderTimeText = styled<React.FC<TypographyProps>>((props) => (
  <Typography noWrap {...props} />
))(({ theme }) => ({
  ...theme.typography.labelReg,
  [theme.breakpoints.up("mobile")]: {
    ...theme.typography.bodySecondaryReg,
  },
}));

interface NoteSectionProps extends StackProps {
  disabled?: boolean;
  tooltip?: string;
}

export const NoteSection = styled(
  ({ disabled: _, tooltip, ...props }: NoteSectionProps) => (
    <TooltipWrapper wrapperSx={{ width: "100%" }} followCursor title={tooltip}>
      <Stack {...props} />
    </TooltipWrapper>
  )
)(({ disabled, theme }) => ({
  border: "1px solid",
  borderColor: theme.palette.shades.neutral[100],
  borderRadius: theme.corners.base,
  padding: "8px 12px",
  backgroundColor: disabled ? theme.palette.shades.neutral[50] : "#fff",
  pointerEvents: disabled ? "none" : "auto",
  marginTop: theme.spacing(2),
  "& .note-section__action": {
    opacity: 0, // uses opacity so the height is accounted for when not visible
    pointerEvents: "none",
    transition: "opacity 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
  },
  "&:hover": {
    "& .note-section__action": {
      opacity: 1,
      pointerEvents: "auto",
    },
  },
  "&.editing": {
    border: "none",
    "&:not(:last-of-type)": {
      borderBottom: "1px solid",
      borderColor: theme.palette.shades.neutral[100],
      borderRadius: 0,
    },
    "& .icon-heading__icon": {
      display: "none",
    },
  },
}));

const NoteSectionHeaderWithRef = forwardRef<HTMLDivElement | null, StackProps>(
  (props, ref) => <Row variant="left" spacing={1} {...props} ref={ref} />
);

NoteSectionHeaderWithRef.displayName = "NoteSectionHeaderWithRef";

export const NoteSectionHeader = styled(NoteSectionHeaderWithRef)({
  padding: "4px 0",
  flex: 1,
});

export const NoteSubSection = styled(Stack)(({ theme }) => ({
  position: "relative",
  [theme.breakpoints.up("mobile")]: {
    padding: theme.spacing(1),
  },
}));

export const Note = styled(Typography)(({ theme }) => ({
  fontFamily: theme.typography.fontFamilySecondary,
  color: theme.palette.shades.neutral[600],
  lineHeight: 19 / 16,
}));

export const EmptyNote = styled(Note)(({ theme }) => ({
  color: theme.palette.shades.neutral[400],
}));

export const BulletedList = styled("ul")({
  padding: 0,
  margin: 0,
});

export const BulletedListItem = styled("li")(({ theme, onClick }) => ({
  marginLeft: "24px",
  fontFamily: theme.typography.fontFamilySecondary,
  color: theme.palette.shades.neutral[600],
  fontSize: "1rem",
  lineHeight: 20 / 16,
  padding: "2px 4px",
  borderRadius: "4px",
  width: "fit-content",
  "&:hover": onClick
    ? {
        cursor: "pointer",
        backgroundColor: theme.palette.shades.ghost[300],
      }
    : { cursor: "default" },
}));

export const DetailContainer = styled<React.FC<StackProps>>((props) => (
  <Stack spacing={1} {...props} />
))(({ theme }) => ({
  marginTop: theme.spacing(2),
  backgroundColor: theme.palette.background.default,
  borderRadius: "4px",
  padding: theme.spacing(1.5),
}));

export const EmptyDetailText = styled<React.FC<StackProps>>(
  ({ children, ...props }) => (
    <Row variant="left" {...props}>
      <span>{children}</span>
    </Row>
  )
)(({ theme }) => ({
  height: "26px",
  fontSize: "0.875rem",
  fontFamily: theme.typography.fontFamilySecondary,
  fontStyle: "italic",
  color: theme.palette.shades.neutral[500],
}));

export const DetailSubHeading = styled<React.FC<StackProps>>(
  ({ children, ...props }) => (
    <Row variant="left" {...props}>
      <span>{children}</span>
    </Row>
  )
)(({ theme }) => ({
  fontSize: "0.875rem",
  height: "26px",
  fontFamily: theme.typography.fontFamilySecondary,
  fontWeight: 500,
  color: theme.palette.shades.neutral[600],
  minWidth: "70px",
}));

export const SelectableChipsContainer = styled(Row)({
  flexWrap: "wrap",
  rowGap: "6px",
  columnGap: "6px",
});

export const SelectableChip = styled(Chip)(({ theme }) => ({
  borderRadius: "4px",
  height: "26px",
  border: `1px solid ${theme.palette.shades.neutral[150]}`,

  "&.MuiChip-outlined": {
    backgroundColor: "#FFF",
    color: theme.palette.shades.neutral[600],
  },

  "& .MuiChip-label": {
    padding: "2px 4px",
    fontFamily: theme.typography.fontFamilySecondary,
  },
}));
